import { defineModule } from '@nexus/core';
import {
  objectCreated,
  objectUpdated,
  projectArchived,
  projectCreated,
  projectStatusChanged,
  projectUpdated,
} from './events';

/** Contract between the module and the kernel (docs/architecture/modules.md). */
export const manifest = defineModule({
  id: 'projects',
  name: 'projects.name',
  description: 'projects.description',
  icon: 'folder-kanban',
  base: true,
  // Clients, owners and external parties are organisations and contacts of crm.
  dependsOn: ['crm'],
  permissions: [
    // Scope "team": only projects with the person in the team (Peter, 11 October 2026).
    { key: 'projects.project.read', scopes: ['team', 'all'] },
    { key: 'projects.project.write', scopes: ['team', 'all'] },
    { key: 'projects.project.comment' },
    { key: 'projects.project.remind' },
    { key: 'projects.project.export' },
    { key: 'projects.project.archive', privileged: true },
    { key: 'projects.project.share', privileged: true },
    { key: 'projects.object.read' },
    { key: 'projects.object.write' },
    { key: 'projects.object.comment' },
    { key: 'projects.object.export' },
    { key: 'projects.object.archive', privileged: true },
    // Plans of objects and projects (manifest `plans`); a role of its own for people on site.
    { key: 'projects.object.plan' },
    { key: 'projects.settings.manage', privileged: true },
  ],
  defaultRoles: [
    {
      key: 'reader',
      name: 'projects.roles.reader',
      // Everyone sees the projects of their own teams and all objects.
      everyone: true,
      grants: [
        { permission: 'projects.project.read', scope: 'team' },
        { permission: 'projects.object.read', scope: null },
      ],
    },
    {
      key: 'editor',
      name: 'projects.roles.editor',
      grants: [
        { permission: 'projects.project.read', scope: 'team' },
        { permission: 'projects.project.write', scope: 'team' },
        { permission: 'projects.project.comment', scope: null },
        { permission: 'projects.project.remind', scope: null },
        { permission: 'projects.project.export', scope: null },
        { permission: 'projects.object.read', scope: null },
        { permission: 'projects.object.write', scope: null },
        { permission: 'projects.object.comment', scope: null },
        { permission: 'projects.object.export', scope: null },
        { permission: 'projects.object.plan', scope: null },
      ],
    },
    {
      key: 'manager',
      name: 'projects.roles.manager',
      grants: [
        // Unscoped permissions of the wildcard ignore the scope.
        { permission: 'projects.project.*', scope: 'all' },
        { permission: 'projects.object.*', scope: null },
        { permission: 'projects.settings.manage', scope: null },
      ],
    },
  ],
  navigation: [
    {
      label: 'projects.nav.projects',
      path: '/projects/projects',
      icon: 'folder-kanban',
      permission: 'projects.project.read',
    },
    {
      label: 'projects.nav.objects',
      path: '/projects/objects',
      icon: 'building-2',
      permission: 'projects.object.read',
    },
    {
      label: 'projects.nav.types',
      path: '/projects/types',
      icon: 'settings',
      permission: 'projects.settings.manage',
    },
  ],
  search: [
    { entity: 'projects.project', permission: 'projects.project.read' },
    { entity: 'projects.object', permission: 'projects.object.read' },
  ],
  activity: [
    {
      entity: 'projects.project',
      read: 'projects.project.read',
      comment: 'projects.project.comment',
      remind: 'projects.project.remind',
      path: '/projects/projects/$id',
    },
    {
      entity: 'projects.object',
      read: 'projects.object.read',
      comment: 'projects.object.comment',
      path: '/projects/objects/$id',
    },
  ],
  files: [
    { entity: 'projects.project', read: 'projects.project.read', write: 'projects.project.write' },
    { entity: 'projects.object', read: 'projects.object.read', write: 'projects.object.write' },
  ],
  // Floor and site plans (docs/architecture/plans.md). Changing plans of a project also needs
  // write access to the project (ProjectData.access).
  plans: [
    { entity: 'projects.object', read: 'projects.object.read', write: 'projects.object.plan' },
    { entity: 'projects.project', read: 'projects.project.read', write: 'projects.object.plan' },
  ],
  customFields: [
    {
      entity: 'projects.project',
      label: 'projects.entities.project',
      read: 'projects.project.read',
      write: 'projects.project.write',
    },
    {
      entity: 'projects.object',
      label: 'projects.entities.object',
      read: 'projects.object.read',
      write: 'projects.object.write',
    },
  ],
  views: [
    {
      list: 'projects.projects',
      read: 'projects.project.read',
      share: 'projects.project.write',
    },
    { list: 'projects.objects', read: 'projects.object.read', share: 'projects.object.write' },
  ],
  workflows: [
    {
      entity: 'projects.project',
      read: 'projects.project.read',
      path: '/projects/projects/$id',
      initial: 'acquisition',
      states: [
        { key: 'acquisition', label: 'projects.states.acquisition', tone: 'info' },
        { key: 'ordered', label: 'projects.states.ordered', tone: 'info' },
        { key: 'active', label: 'projects.states.active', tone: 'success' },
        { key: 'paused', label: 'projects.states.paused', tone: 'warning' },
        { key: 'closed', label: 'projects.states.closed', tone: 'neutral', final: true },
        { key: 'cancelled', label: 'projects.states.cancelled', tone: 'danger', final: true },
      ],
      transitions: [
        {
          key: 'order',
          label: 'projects.transitions.order',
          from: ['acquisition'],
          to: 'ordered',
          permission: 'projects.project.write',
        },
        {
          key: 'start',
          label: 'projects.transitions.start',
          from: ['acquisition', 'ordered', 'paused'],
          to: 'active',
          permission: 'projects.project.write',
        },
        {
          key: 'pause',
          label: 'projects.transitions.pause',
          from: ['active'],
          to: 'paused',
          permission: 'projects.project.write',
          reason: 'optional',
        },
        {
          key: 'close',
          label: 'projects.transitions.close',
          from: ['ordered', 'active', 'paused'],
          to: 'closed',
          permission: 'projects.project.write',
        },
        {
          key: 'cancel',
          label: 'projects.transitions.cancel',
          from: ['acquisition', 'ordered', 'active', 'paused'],
          to: 'cancelled',
          permission: 'projects.project.write',
          reason: 'required',
        },
      ],
    },
  ],
  sharing: [
    {
      entity: 'projects.project',
      read: 'projects.project.read',
      share: 'projects.project.share',
      path: '/projects/projects/$id',
      parts: ['files', 'activity'],
    },
  ],
  sequences: [
    {
      key: 'projects.project',
      label: 'projects.sequences.project',
      format: 'P-{year}-{n:3}',
      manualEntry: true,
    },
    // Typed in by hand the number stays optional (ObjectService.create).
    {
      key: 'projects.object',
      label: 'projects.sequences.object',
      format: 'OBJ-{n:4}',
      manualEntry: true,
    },
  ],
  // Read-only on devices, so inspections of other modules work without a connection
  // (ObjectOffline, ProjectOffline). Only prepared objects and projects and those that records on
  // the device name, e.g. the object of a prepared inspection, with their customers.
  offline: [
    {
      entity: 'projects.object',
      label: 'projects.offline.objects',
      mode: 'read',
      scope: 'prepared',
      conflict: 'last-write-wins',
      maxAge: 'P30D',
      permissions: { read: 'projects.object.read' },
      pins: [{ entity: 'projects.object', field: 'id' }],
      references: [
        { entity: 'crm.organization', field: 'ownerId' },
        { entity: 'crm.organization', field: 'operatorId' },
      ],
    },
    {
      entity: 'projects.project',
      label: 'projects.offline.projects',
      mode: 'read',
      // Within the scope of projects.project.read (teams or all), without projects other
      // companies share.
      scope: 'prepared',
      conflict: 'last-write-wins',
      maxAge: 'P30D',
      permissions: { read: 'projects.project.read' },
      pins: [{ entity: 'projects.project', field: 'id' }],
      references: [{ entity: 'crm.organization', field: 'clientId' }],
    },
  ],
  // Places on the object and project pages where modules show sections, e.g. inspections.
  sectionTargets: ['projects.object', 'projects.project'],
  recordSections: [
    {
      key: 'projects.customer-projects',
      target: 'crm.organization',
      title: 'projects.sections.customerProjects',
      permission: 'projects.project.read',
      order: 10,
    },
    {
      key: 'projects.customer-objects',
      target: 'crm.organization',
      title: 'projects.sections.customerObjects',
      permission: 'projects.object.read',
      icon: 'building-2',
      order: 20,
    },
  ],
  events: {
    publishes: [
      projectCreated.type,
      projectUpdated.type,
      projectStatusChanged.type,
      projectArchived.type,
      objectCreated.type,
      objectUpdated.type,
    ],
    subscribes: ['crm.organization.merged'],
  },
  dataProcessing: [
    {
      entity: 'projects.project',
      purpose:
        'Projektstamm für Planung, Gutachten und Betreuung: Auftraggeber, Beteiligte, Team und Leistungen eines Projekts; Verteiler für Mails zum Projekt, z. B. Protokollempfang',
      legalBasis: 'Art. 6 Abs. 1 lit. b, c und f DSGVO',
      // Team members are account IDs, external parties references to crm. The address of a
      // private building owner is held by the object. Distribution lists hold references to crm
      // or name and business address pasted from a mail program.
      categories: ['account-id', 'contact-reference', 'name', 'business-contact', 'free-text'],
      // Commercial records ten years after the project ended (§ 147 AO); warranty claims for
      // planning work expire five years after acceptance (§ 634a BGB). Confirm with the DPO.
      retention: { period: 'P10Y', after: 'closed' },
      erase: 'anonymize',
    },
    {
      entity: 'projects.object',
      purpose:
        'Gebäude und Liegenschaften, an denen Projekte stattfinden, mit Anschrift, Eigentümer und Gebäudedaten',
      legalBasis: 'Art. 6 Abs. 1 lit. b und f DSGVO',
      // The building of a private owner is their home address, with coordinates.
      categories: ['postal-address', 'geo-location', 'contact-reference', 'free-text'],
      retention: { period: 'P10Y', after: 'last-project-closed' },
      erase: 'anonymize',
    },
  ],
});
